import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { Pagination } from "@/design-system";
import { listReceipts } from "@/infrastructure/receipt/receiptService";
import { listMovementTypes, listMovementSubtypes } from "@/infrastructure/movement/movementService";
import { listWarehouses } from "@/infrastructure/warehouse/warehouseService";
import type { Receipt, ProductMeta, MovementSubtype, MovementType, WarehouseRecord, ReceiptStatus } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { formatDate } from "@/utils/formatDate";

const PAGE_SIZE = 8;

export const ReceiptsPage = memo(function ReceiptsPage({ onOpenDetail }: { onOpenDetail: (id: string) => void }) {
  const { t } = useI18n();
  const { canCreate } = useRole();

  const STATUS_LABELS: Record<ReceiptStatus, { bg: string; color: string; label: string }> = {
    DRAFT: { bg: "rgba(245,158,11,0.1)", color: "#d97706", label: t.receipts.statusDraft },
    CONFIRMED: { bg: "rgba(34,197,94,0.1)", color: "#16a34a", label: t.receipts.statusConfirmed },
    CANCELLED: { bg: "rgba(239,68,68,0.1)", color: "#dc2626", label: t.receipts.statusCancelled },
  };
  const [items, setItems] = useState<Receipt[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [subtypes, setSubtypes] = useState<MovementSubtype[]>([]);
  const [types, setTypes] = useState<MovementType[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [receiptData, stData, tData, whData] = await Promise.all([
        listReceipts({ page, limit: PAGE_SIZE, status: statusFilter || undefined }),
        subtypes.length === 0 ? listMovementSubtypes() : Promise.resolve(subtypes),
        types.length === 0 ? listMovementTypes() : Promise.resolve(types),
        warehouses.length === 0 ? listWarehouses() : Promise.resolve(warehouses),
      ]);
      setItems(receiptData.items);
      setMeta(receiptData.meta);
      if (subtypes.length === 0) setSubtypes(stData as MovementSubtype[]);
      if (types.length === 0) setTypes(tData as MovementType[]);
      if (warehouses.length === 0) setWarehouses(whData as WarehouseRecord[]);
    } catch {}
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getSubtypeName = useCallback((id: string) => subtypes.find(s => s.id === id)?.name ?? "—", [subtypes]);
  const getWarehouseName = useCallback((id: string) => warehouses.find(w => w.warehouse_id === id)?.warehouse_name ?? "—", [warehouses]);

  const inTypeId = types.find(t => t.code === "IN" || t.sign === 1)?.id;
  const inSubtypes = subtypes.filter(s => s.status && s.movementTypeId === inTypeId);

  const showToast = useCallback((msg: string, type: "success" | "error") => { setToast({ message: msg, type }); setTimeout(() => setToast(null), 3500); }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.platform.receipts} ({meta.totalItems})</h1>
        </div>
        {canCreate && <button onClick={() => setShowCreate(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover)} onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primary)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          {t.receipts.newReceipt}
        </button>}
      </div>

      <div style={{ display: "flex", gap: "4px" }}>
        {(["", "DRAFT", "CONFIRMED", "CANCELLED"] as const).map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: statusFilter === s ? typography.weight.semibold : typography.weight.medium, padding: "8px 16px", borderRadius: "8px", border: `1px solid ${statusFilter === s ? "#111" : "#e8e8e8"}`, background: statusFilter === s ? "#111" : "#fff", color: statusFilter === s ? "#fff" : "#666", cursor: "pointer", transition: `all ${transitions.fast}` }}>
            {s === "" ? t.receipts.filterAll : STATUS_LABELS[s].label}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.receipts.colReceiptNum, t.receipts.colSubtype, t.receipts.colWarehouse, t.receipts.colDate, t.receipts.colStatus].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 16px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const cfg = STATUS_LABELS[item.status];
                  return (
                    <tr key={item.id} onClick={() => onOpenDetail(item.id)} style={{ borderBottom: "1px solid #f7f7f7", cursor: "pointer", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.receiptNumber}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{getSubtypeName(item.movementSubtypeId)}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{getWarehouseName(item.warehouseId)}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{formatDate(item.createdAt)}</span></td>
                      <td style={cell}><span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: cfg.bg, color: cfg.color, fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}><span style={{ width: "5px", height: "5px", borderRadius: "50%", background: cfg.color }} />{cfg.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ padding: "0 16px 14px" }}>
                <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>

      {showCreate && <CreateReceiptModal subtypes={inSubtypes} warehouses={warehouses} onCreated={(id) => { setShowCreate(false); onOpenDetail(id); fetchData(); }} onClose={() => setShowCreate(false)} />}
      {toast && <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: toast.type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300 }}>{toast.message}</div>}
    </div>
  );
});

function CreateReceiptModal({ subtypes, warehouses, onCreated, onClose }: { subtypes: MovementSubtype[]; warehouses: WarehouseRecord[]; onCreated: (id: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [warehouseId, setWarehouseId] = useState("");
  const [subtypeId, setSubtypeId] = useState("");
  const [referenceDoc, setReferenceDoc] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h); }, [onClose]);

  const isValid = !!warehouseId && !!subtypeId && !!referenceDoc.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      const { createReceipt } = await import("@/infrastructure/receipt/receiptService");
      const receipt = await createReceipt({ warehouseId, movementSubtypeId: subtypeId, referenceDoc: referenceDoc.trim() || undefined, notes: notes.trim() || undefined });
      onCreated(receipt.id);
    } catch { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.receipts.newReceipt}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Sel label={`${t.receipts.subtypeLabel} *`} value={subtypeId} onChange={setSubtypeId} placeholder={t.receipts.selectSubtype} options={subtypes.map(s => ({ value: s.id, label: s.name }))} />
          <Sel label={`${t.receipts.warehouseLabel} *`} value={warehouseId} onChange={setWarehouseId} placeholder={t.receipts.selectWarehouse} options={warehouses.map(w => ({ value: w.warehouse_id, label: w.warehouse_name }))} />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.receipts.reference} *</label>
            <input value={referenceDoc} onChange={e => setReferenceDoc(e.target.value)} placeholder={t.receipts.referencePlaceholder} style={inputStyle} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.receipts.notes}</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder={t.receipts.notesPlaceholder} style={inputStyle} />
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.receipts.cancel}</button>
            <button type="submit" disabled={!isValid || saving} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isValid && !saving ? "#111" : "#ccc", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isValid && !saving ? "pointer" : "not-allowed" }}>{t.receipts.createDraft}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" };
const cell: React.CSSProperties = { padding: "14px 16px", verticalAlign: "middle" };

function Sel({ label, value, onChange, placeholder, options }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; options: { value: string; label: string }[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, color: value ? "#111" : "#aaa" }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: "10px" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.receipts.emptyList}</span></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}
